import { useEffect, useMemo, useState } from "react";

const useUserId = () => {
  const [userInfo, setUserInfo] = useState(() => {
    // Initialize synchronously so consumers don't see a null user when a token already exists
    try {
      const info = getUserInfoFromAccessToken();
      return info || { userId: null, role: null };
    } catch {
      return { userId: null, role: null };
    }
  });

  const getUserInfoFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return { userId: null, role: null };
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userId = decodedToken.user_id;
      const role = decodedToken.role;
      return { userId, role };
    } catch (error) {
      console.error("Failed to decode access token", error);
      return { userId: null, role: null };
    }
  };

  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  };

  useEffect(() => {
    // Read immediately on mount
    setUserInfo(getUserInfoFromAccessToken());

    // Listen to token changes across tabs/windows
    const handleStorage = (e) => {
      if (e.key === "accessToken") {
        setUserInfo(getUserInfoFromAccessToken());
      }
    };
    window.addEventListener("storage", handleStorage);

    // Allow same-tab flows to signal token changes
    const handleAuthTokenChanged = () => {
      setUserInfo(getUserInfoFromAccessToken());
    };
    window.addEventListener("auth-token-changed", handleAuthTokenChanged);

    // As a safety net, poll briefly after mount in case login just set token
    const timeoutId = setTimeout(() => {
      setUserInfo(getUserInfoFromAccessToken());
    }, 0);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("auth-token-changed", handleAuthTokenChanged);
      clearTimeout(timeoutId);
    };
  }, []);

  return userInfo;
};

export default useUserId;